"use client";

import Link from "next/link";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  Droplets,
  Hammer,
  Home,
  KeyRound,
  LineChart,
  Plus,
  ReceiptText,
  TrendingUp,
  WalletCards,
} from "lucide-react";

import {
  RentComparisonPanel,
  type RentComparisonOption,
} from "@/components/portfolio/RentComparisonPanel";
import { Button } from "@/components/ui/Button";
import { DemoBrushBadge } from "@/components/ui/DemoBrushBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { storePendingAuthMode } from "@/lib/auth-navigation";
import { PublicHeroChecklist, PublicHeroCopy } from "@/components/PublicHero";
import { UpcomingPortfolioSummary } from "@/components/portfolio/UpcomingPortfolioSummary";

const properties = [
  {
    name: "Byt Letná",
    meta: "Pronajímáno · 2+kk",
    value: "7,84 mil. Kč",
    rent: "24 500 Kč",
    yield: "+4,7 %",
    trendPath:
      "M0 68 C24 66, 35 55, 58 57 S88 43, 112 48 S145 32, 170 35 S204 18, 240 13",
    color: "bg-softiq-secondary",
  },
  {
    name: "Dům Říčany",
    meta: "Vlastní bydlení · 5+kk",
    value: "12,60 mil. Kč",
    rent: "—",
    yield: "+6,1 %",
    trendPath:
      "M0 69 C27 62, 37 65, 58 51 S93 53, 116 37 S151 31, 176 25 S211 15, 240 6",
    color: "bg-softiq-secondary-shade-100",
  },
  {
    name: "Studio Karlín",
    meta: "Pronajímáno · 1+kk",
    value: "5,21 mil. Kč",
    rent: "19 900 Kč",
    yield: "+3,9 %",
    trendPath:
      "M0 64 C22 57, 42 61, 63 54 S94 38, 118 42 S143 66, 170 60 S207 27, 240 17",
    color: "bg-softiq-secondary-shade-300",
  },
] as const;

const featureRows = [
  {
    icon: ReceiptText,
    title: "Příjmy a výdaje",
    text: "Nájemné, fond oprav, pojištění i drobné opravy v jednom cash flow.",
  },
  {
    icon: Droplets,
    title: "Spotřeba a provoz",
    text: "Voda, energie a pravidelné odečty bez hledání ve starých fakturách.",
  },
  {
    icon: CalendarClock,
    title: "Co vás čeká",
    text: "Budoucí platby, revize, smlouvy a termíny s upozorněním dopředu.",
  },
  {
    icon: LineChart,
    title: "Hodnota v čase",
    text: "Aktuální tržní odhad i vývoj hodnoty každé nemovitosti a celého portfolia.",
  },
] as const;

function Eyebrow({
  children,
  inverse = false,
}: {
  children: React.ReactNode;
  inverse?: boolean;
}) {
  return (
    <p
      className={cn(
        "text-[11px] font-medium uppercase tracking-[0.2em]",
        inverse
          ? "public-hero-eyebrow text-white/55"
          : "text-softiq-primary/60 dark:text-white/55",
      )}
    >
      {children}
    </p>
  );
}

export function PortfolioPreview({
  lightSurface = false,
}: {
  lightSurface?: boolean;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const shouldReduceMotion = useReducedMotion() ?? false;
  const selected = selectedIndex == null ? null : properties[selectedIndex];
  const trendPath =
    selected?.trendPath ??
    "M0 66 C20 64, 34 53, 58 55 S88 42, 112 46 S145 32, 172 35 S205 15, 240 7";
  const openRegistration = () => {
    storePendingAuthMode("register");
    window.location.assign("/#prihlaseni");
  };

  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[590px] border-l pl-4 text-softiq-primary dark:text-white sm:pl-6",
        lightSurface
          ? "border-softiq-border-subtle dark:border-white/20"
          : "border-white/20",
      )}
    >
      <div className="app-section-card public-summary-banner relative w-full">
        <div className="app-section-divider-bottom flex items-center justify-between px-5 py-4 sm:px-6">
          <div>
            <div className="flex items-center gap-2">
              <p className="app-section-eyebrow public-banner-eyebrow">
                Moje portfolio
              </p>
              <DemoBrushBadge />
            </div>
            <p className="app-section-heading mt-1">3 nemovitosti</p>
          </div>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="unstyled"
                  onClick={openRegistration}
                  aria-label="Přidat nemovitost"
                  className="app-section-add-button group"
                >
                  <Building2
                    className="public-portfolio-banner-icon size-4"
                    aria-hidden
                  />
                  <span className="app-section-add-badge">
                    <Plus
                      aria-hidden
                      className="size-2.5 stroke-[2.5] text-softiq-primary transition-transform group-hover:rotate-90"
                    />
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={15}>
                Přidat nemovitost
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="border-b border-softiq-primary/10 p-5 dark:border-white/10 sm:p-6 lg:border-b-0 lg:border-r">
            <p className="app-section-description">
              {selected?.name ?? "Celková hodnota"}
            </p>
            <motion.p
              key={selected?.name ?? "portfolio-total"}
              initial={
                shouldReduceMotion ? false : { opacity: 0, y: 8, scale: 0.94 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 260, damping: 18, mass: 0.7 }
              }
              className="mt-2 origin-left text-[2rem] font-medium leading-none tracking-[-0.05em]"
            >
              {selected?.value ?? "25,65 mil."}
            </motion.p>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-softiq-secondary-shade-200 px-2 py-1 font-medium dark:bg-white/10 dark:text-softiq-secondary">
                <TrendingUp className="size-3" /> {selected?.yield ?? "5,2 %"}
              </span>
              <span className="app-meta-muted">za 12 měsíců</span>
            </div>
            <svg
              className="mt-7 h-20 w-full overflow-visible text-softiq-primary dark:text-softiq-secondary"
              viewBox="0 0 240 80"
              role="img"
              aria-label={
                selected
                  ? `Rostoucí vývoj hodnoty nemovitosti ${selected.name}`
                  : "Rostoucí vývoj hodnoty portfolia"
              }
            >
              <motion.path
                initial={false}
                animate={{ d: trendPath }}
                transition={
                  shouldReduceMotion
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 95, damping: 19 }
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <motion.path
                initial={false}
                animate={{ d: `${trendPath} L240 80 L0 80 Z` }}
                transition={
                  shouldReduceMotion
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 95, damping: 19 }
                }
                fill="url(#portfolio-fill)"
                opacity=".22"
              />
              <defs>
                <linearGradient id="portfolio-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop stopColor="currentColor" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="p-3 sm:p-4">
            <div className="space-y-1">
              {properties.map((property, index) => (
                <button
                  key={property.name}
                  type="button"
                  onClick={() =>
                    setSelectedIndex(selectedIndex === index ? null : index)
                  }
                  className={cn(
                    "group flex w-full items-center gap-3 rounded-xl border-b border-softiq-primary/10 px-3 py-3 text-left transition-colors last:border-b-0 dark:border-white/10",
                    selectedIndex === index
                      ? "bg-white dark:bg-white/10"
                      : "hover:bg-white/60 dark:hover:bg-white/[0.05]",
                  )}
                  aria-pressed={selectedIndex === index}
                >
                  <span
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-lg",
                      property.color,
                      "dark:bg-white/10 dark:text-softiq-secondary",
                    )}
                  >
                    {index === 1 ? (
                      <Home className="size-4" />
                    ) : (
                      <KeyRound className="size-4" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">
                      {property.name}
                    </span>
                    <span className="app-section-meta block truncate">
                      {property.meta}
                    </span>
                  </span>
                  <ChevronRight
                    className={cn(
                      "size-4 transition-transform",
                      selectedIndex === index && "translate-x-0.5",
                    )}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ValuationCalculator() {
  const [hiddenSuggestions, setHiddenSuggestions] = useState<string[]>([]);
  const hideSuggestion = (key: string) =>
    setHiddenSuggestions((current) =>
      current.includes(key) ? current : [...current, key],
    );
  const options: RentComparisonOption[] = [
    {
      id: "current",
      label: "Aktuální",
      rent: "16 300 Kč",
      increase: "Beze změny",
    },
    {
      id: "suggested",
      label: "Doporučený",
      rent: "16 500 Kč",
      increase: "+200 Kč",
      increaseTone: "positive",
      percentage: { valueNow: 16_500, valueBefore: 16_300 },
    },
    {
      id: "renovated",
      label: "Po rekonstrukci",
      rent: "16 500 Kč",
      increase: "+200 Kč",
      increaseTone: "positive",
      percentage: { valueNow: 16_500, valueBefore: 16_300 },
    },
  ];

  return (
    <RentComparisonPanel
      valueLabel="Odhad hodnoty"
      currentValueLabel="Aktuální"
      currentValue="6 580 000 Kč"
      renovatedValueLabel="Po rekonstrukci"
      renovatedValue="6 660 000 Kč"
      valueIncreaseLabel="Nárůst"
      valueIncrease="+80 000 Kč"
      valuePercentage={{ valueNow: 6_660_000, valueBefore: 6_580_000 }}
      valueSuggestions={
        hiddenSuggestions.includes("renovationValue")
          ? []
          : [
              {
                key: "renovationValue",
                label: "Rekonstrukce se nevyplatí",
                dismissLabel: "Skrýt toto doporučení",
                onDismiss: () => hideSuggestion("renovationValue"),
              },
            ]
      }
      rentSuggestions={
        hiddenSuggestions.includes("renovationRent")
          ? []
          : [
              {
                key: "renovationRent",
                label: "Rekonstrukce nájem nezvýší",
                dismissLabel: "Skrýt toto doporučení",
                onDismiss: () => hideSuggestion("renovationRent"),
              },
            ]
      }
      rentLabel="Nájem / měsíc"
      options={options}
    />
  );
}

export default function PortfolioHomePage() {
  const handleRegistrationClick = () => storePendingAuthMode("register");

  return (
    <div className="public-editorial overflow-hidden bg-background text-foreground">
      <section className="relative !px-0 !py-0">
        <div className="absolute inset-0 bg-softiq-surface-muted dark:bg-softiq-secondary-shade-900" />
        <div className="relative mx-auto grid min-h-[720px] max-w-7xl items-center gap-16 px-6 pb-20 pt-12 sm:pt-14 lg:grid-cols-[1.14fr_0.86fr] lg:px-10 lg:pb-24 lg:pt-16">
          <div className="public-hero-copy max-w-xl text-softiq-copy lg:order-2">
            <PublicHeroCopy
              eyebrow="Správa portfolia pro majitele nemovitostí"
              title="Každá nemovitost."
              accent="Spravujte ji přehledně."
              description="Hodnota, náklady, nájem, spotřeba i další platby. Pro celé investiční portfolio i domov, ve kterém právě žijete."
              tone="light"
            />
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild
                variant="brand"
                size="lg"
                className="h-12 rounded-full px-6 text-softiq-primary"
              >
                <Link href="/#prihlaseni" onClick={handleRegistrationClick}>
                  Začít zdarma <ArrowRight />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 rounded-full border-softiq-border bg-transparent px-6 text-softiq-primary hover:bg-softiq-secondary-shade-100/70 dark:border-white/25 dark:bg-transparent dark:text-white dark:hover:bg-white/10"
              >
                <Link href="#jak-to-funguje">Prohlédnout funkce</Link>
              </Button>
            </div>
            <PublicHeroChecklist
              items={["Byt, dům i pronájem", "Vše v Kč a na jednom místě"]}
              tone="light"
            />
          </div>
          <div className="lg:order-1">
            <PortfolioPreview />
          </div>
        </div>
      </section>

      <section className="!px-0 !py-0">
        <div className="mx-auto grid max-w-7xl divide-y border-b border-softiq-primary/10 px-6 dark:border-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-10">
          {[
            ["01", "Portfolio", "Všechny nemovitosti pohromadě"],
            ["02", "Provoz", "Peníze, spotřeba a termíny"],
            ["03", "Potenciál", "Hodnota a výnos po rekonstrukci"],
          ].map(([number, title, text]) => (
            <div
              key={number}
              className="flex items-start gap-5 py-7 sm:px-7 first:pl-0 last:pr-0"
            >
              <span className="font-display text-xl italic text-softiq-primary/35 dark:text-white/35">
                {number}
              </span>
              <div>
                <p className="text-sm font-medium">{title}</p>
                <p className="mt-1 text-xs text-foreground/45">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="jak-to-funguje" className="!px-0 !py-0">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-[0.9fr_1.1fr] lg:px-10 lg:py-32">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <Eyebrow>Každodenní přehled</Eyebrow>
            <h2 className="mt-5 max-w-lg !text-left text-[clamp(2.7rem,5vw,5rem)] !font-normal leading-[0.95] tracking-[-0.05em]">
              Co nemovitost stojí. A co vám přináší.
            </h2>
            <p className="mt-7 max-w-md text-sm leading-7 text-foreground/55">
              SOFTIQ spojuje běžný provoz s dlouhodobým pohledem. Už nemusíte
              skládat realitu z tabulek, faktur a poznámek.
            </p>
          </div>
          <div className="border-t border-softiq-primary/15 dark:border-white/15">
            {featureRows.map((item, index) => (
              <div
                key={item.title}
                className="group grid gap-4 border-b border-softiq-primary/15 py-7 transition-colors dark:border-white/15 sm:grid-cols-[56px_0.7fr_1fr] sm:items-start"
              >
                <span className="font-display text-lg italic text-foreground/30">
                  0{index + 1}
                </span>
                <div className="flex items-center gap-3">
                  <item.icon className="size-4 text-softiq-secondary-shade-700 dark:text-softiq-secondary" />
                  <h3 className="text-base font-medium">{item.title}</h3>
                </div>
                <p className="text-sm leading-6 text-foreground/50">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="!px-0 !py-0 bg-softiq-surface dark:bg-softiq-dark-surface">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-end">
            <div>
              <Eyebrow>Pro každou situaci</Eyebrow>
              <h2 className="mt-5 max-w-2xl !text-left text-[clamp(2.7rem,5vw,5rem)] !font-normal leading-[0.95] tracking-[-0.05em]">
                Investor i nájemník. Portfolio i jeden domov.
              </h2>
            </div>
            <p className="max-w-lg text-sm leading-7 text-foreground/55 lg:justify-self-end">
              Nemusíte vlastnit deset bytů. Přehled dává smysl i pro jeden
              pronajímaný byt nebo domácnost, ve které chcete mít náklady pod
              kontrolou.
            </p>
          </div>
          <div className="mt-16 grid border-y border-softiq-primary/15 dark:border-white/15 lg:grid-cols-2 lg:divide-x lg:divide-softiq-primary/15 dark:lg:divide-white/15">
            <div className="py-10 lg:pr-12">
              <Building2 className="size-7 text-softiq-secondary-shade-700 dark:text-softiq-secondary" />
              <h3 className="app-feature-title mt-10">
                Pronajímám a investuji
              </h3>
              <p className="app-feature-description mt-4 max-w-lg">
                Sledujte obsazenost, nájemné, výnos, financování a všechny
                výdaje každé jednotky.
              </p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Cash flow každé nemovitosti",
                  "Nájemní smlouvy a budoucí platby",
                  "Tržní nájemné a výnos po rekonstrukci",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <Check className="size-4 text-softiq-secondary-shade-700" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-softiq-primary/15 py-10 dark:border-white/15 lg:border-t-0 lg:pl-12">
              <Home className="size-7 text-softiq-secondary-shade-700 dark:text-softiq-secondary" />
              <h3 className="app-feature-title mt-10">
                Bydlím ve vlastním nebo v nájmu
              </h3>
              <p className="app-feature-description mt-4 max-w-lg">
                Mějte přehled o bydlení, i když není investicí. Náklady,
                spotřeba i termíny zůstanou po ruce.
              </p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Voda, energie a provoz domácnosti",
                  "Hypotéka nebo nájem a pravidelné výdaje",
                  "Revize, pojištění a důležité termíny",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <Check className="size-4 text-softiq-secondary-shade-700" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="!px-0 !py-0">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-[0.75fr_1.25fr] lg:px-10 lg:py-32">
          <div>
            <div className="flex size-12 items-center justify-center rounded-full bg-softiq-secondary-shade-100 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
              <Hammer className="size-5" />
            </div>
            <Eyebrow>Nájem a hodnota</Eyebrow>
            <h2 className="mt-5 max-w-lg !text-left text-[clamp(2.7rem,4.8vw,4.7rem)] !font-normal leading-[0.96] tracking-[-0.05em]">
              Porovnání současného nájmu s trhem
            </h2>
          </div>
          <ValuationCalculator />
        </div>
      </section>

      <section className="!px-0 !py-0 bg-softiq-primary text-white dark:bg-softiq-dark-surface">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
          <UpcomingPortfolioSummary />
        </div>
      </section>

      <section className="!px-0 !py-0">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center lg:px-10 lg:py-32">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full border border-softiq-primary/15 dark:border-white/15">
            <WalletCards className="size-5" />
          </div>
          <h2 className="mx-auto mt-8 max-w-4xl text-[clamp(3rem,6vw,6rem)] !font-normal leading-[0.9] tracking-[-0.055em]">
            Mějte jasno v tom, co vlastníte.{" "}
            <span className="font-sans font-medium not-italic text-softiq-secondary-shade-700 dark:text-softiq-secondary">
              I co to může přinést.
            </span>
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-sm leading-7 text-foreground/55">
            Přidejte první nemovitost a začněte stavět přehled, který roste
            spolu s vaším portfoliem.
          </p>
          <Button
            asChild
            variant="default"
            size="lg"
            className="mt-9 h-12 rounded-full px-7"
          >
            <Link href="/#prihlaseni" onClick={handleRegistrationClick}>
              Vytvořit moje portfolio <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
